import * as React from "react";
import { useNavigate } from "react-router";

import { useAuth } from "@/providers";
import {
  TemplateVariableType,
  type AgentRecord,
} from "@/shared/domain/agents/AgentRecord";
import { useAgents } from "@/shared/domain/agents/hooks";
import { Button } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import styles from "./LaunchAgentForDatasetButton.module.css";

interface LaunchAgentForDatasetButtonProps {
  /** Identifier of the dataset to pre-fill on the launch page. */
  datasetId: string;
}

/**
 * Click-to-open menu that lists every Agent in the caller's org whose
 * variable set contains at least one ``DATASET_ID``-typed variable.
 * Selecting an agent navigates to that agent's launch page with the
 * current dataset id seeded as a query param — the launch page reads
 * ``?dataset_id=`` in :file:`LaunchAgent.tsx` and feeds it through
 * ``seedLaunchValues`` to every DATASET_ID input.
 *
 * Render contract by query state:
 *
 * * ``isPending`` (initial load) or ``isError``: render nothing. The
 *   button is a progressive-enhancement affordance; we'd rather have
 *   it appear on resolve than show a skeleton or a disabled button
 *   that the user might click before the agents list is ready.
 * * loaded with no DATASET_ID-capable agents: render nothing. An
 *   unused button on a tab the user can already use to read existing
 *   threads adds visual noise without offering a path forward.
 * * loaded with qualifying agents: render the trigger in the
 *   threads-table toolbar.
 *
 * Visual + a11y modeled on :class:`CreateAgentMenu`: a plain
 * click-to-show panel with ``aria-haspopup`` and Escape-to-close. No
 * ``role="menu"`` / ``role="menuitem"`` markup because the panel does
 * not implement arrow-key, Home/End, type-ahead, or roving tabindex
 * semantics — advertising the menu role without those would lie to
 * assistive tech.
 */
export function LaunchAgentForDatasetButton({
  datasetId,
}: LaunchAgentForDatasetButtonProps) {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;
  // ``useAgents`` returns a single page. The backend route caps the
  // page at 100 (hardcoded in ``ai_router.py``'s ``list_agents`` — the
  // route doesn't expose ``limit`` as a query param), so an org with
  // more than 100 agents would silently truncate the dropdown. For v1
  // we accept that; lifting the cap means paging on menu open (and
  // likely exposing ``limit`` on the route).
  const agentsQuery = useAgents({ orgId });

  const candidateAgents = React.useMemo<AgentRecord[]>(() => {
    if (!agentsQuery.data) {
      return [];
    }
    return agentsQuery.data.items.filter((agent) =>
      agent.variables.some(
        (variable) => variable.type === TemplateVariableType.DATASET_ID,
      ),
    );
  }, [agentsQuery.data]);

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const mouseHandler = (event: MouseEvent) => {
      if (
        anchorRef.current !== null &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", mouseHandler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", mouseHandler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  // Hide while the agents list is loading or errored as well as when
  // the loaded list has no DATASET_ID-capable agents — see the render
  // contract in the component docstring.
  if (
    agentsQuery.isPending ||
    agentsQuery.isError ||
    candidateAgents.length === 0
  ) {
    return null;
  }

  const handleSelect = (agent: AgentRecord) => {
    setOpen(false);
    void navigate(Goto.createAgentLaunchRoute(agent.agent_id, { datasetId }));
  };

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <Button
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        Launch
      </Button>
      {open && (
        <div className={styles.popover}>
          {candidateAgents.map((agent) => (
            <button
              key={agent.agent_id}
              type="button"
              className={styles.option}
              onClick={() => handleSelect(agent)}
            >
              <span className={styles.optionLabel}>{agent.name}</span>
              {agent.description ? (
                <span className={styles.optionDescription}>
                  {agent.description}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
